import { ApolloClient, InMemoryCache } from "@apollo/client";
import {
  GET_WORKFLOW_DESIGN_TICKET_QUERY,
  LIST_WORKFLOW_DESIGN_TICKETS_QUERY,
  CREATE_WORKFLOW_DESIGN_MUTATION,
} from "../queries/workflowDesignSubmissionQueries";

// GraphQL endpoint for Workflow Design Submission
const WORKFLOW_DESIGN_SUBMISSION_ENDPOINT =
  "https://zpk2luj33jeu5iaelz3qfkzekm.appsync-api.us-east-1.amazonaws.com/graphql";

const workflowDesignSubmissionClient = new ApolloClient({
  uri: WORKFLOW_DESIGN_SUBMISSION_ENDPOINT,
  cache: new InMemoryCache(),
  headers: {
    "x-api-key": "da2-m2ijj6nv4zh3hotum2tpkb4poe",
  },
});

// Helper function to map workflow design data to the expected format
const mapWorkflowDesignToCreateInput = (workflowData) => {
  return {
    tenantId: workflowData.tenantId,
    workflowTitle: workflowData.workflowTitle,
    summaryNarrative: workflowData.summaryNarrative,
    priority: workflowData.priority || "medium",
    source: workflowData.source || "workflow_design_summary",
    sourceScreen: workflowData.sourceScreen || "workflow_design_summary_page",
  };
};

// Helper function to map InitialDesignLayoutTicket to component format
const mapTicketToComponent = (ticket) => {
  return {
    ticketId: ticket.ticketId,
    tenantId: ticket.tenantId,
    status: ticket.status,
    workflowTitle: ticket.workflowTitle,
    summaryNarrative: ticket.summaryNarrative,
    priority: ticket.priority || "medium",
    source: ticket.source || "workflow_design_summary",
    sourceScreen: ticket.sourceScreen || "workflow_design_summary_page",
    createdAt: ticket.createdAt,
    updatedAt: ticket.updatedAt,
    completedAt: ticket.completedAt,
    executionArn: ticket.executionArn,
    designId: ticket.designId,
    workflowEntityId: ticket.workflowEntityId,
    metadata: ticket.metadata ? JSON.parse(ticket.metadata) : {},
    // Additional computed fields
    isCompleted: ticket.status === "completed",
    isProcessing: ticket.status === "processing",
    isPending: ticket.status === "pending",
    isFailed: ticket.status === "failed",
    processingTime:
      ticket.completedAt && ticket.createdAt
        ? new Date(ticket.completedAt) - new Date(ticket.createdAt)
        : null,
  };
};

// Create/Submit a new workflow design
export const createWorkflowDesignInApi = async (input) => {
  try {
    console.log(
      `Workflow Design Submission - createWorkflowDesignInApi - input: ${JSON.stringify(
        input
      )}`
    );

    // Map workflow design data to CreateInitialDesignLayoutInput format
    const createInput = mapWorkflowDesignToCreateInput(input);

    const apiPayload = {
      mutation: CREATE_WORKFLOW_DESIGN_MUTATION,
      variables: { input: createInput },
    };

    const response = await workflowDesignSubmissionClient.mutate(apiPayload);
    console.log(
      `Workflow Design Submission - createWorkflowDesignInApi - response: ${JSON.stringify(
        response
      )}`
    );

    const result = response.data.createInitialDesignLayout;

    return {
      success: true,
      ticket: mapTicketToComponent(result),
      ticketId: result.ticketId,
      executionArn: result.executionArn,
      message: "Workflow design submitted successfully",
    };
  } catch (error) {
    console.error(
      `Workflow Design Submission - createWorkflowDesignInApi - error: ${JSON.stringify(
        error
      )}`
    );
    throw error;
  }
};

// Get a specific workflow design ticket
export const getWorkflowDesignTicketFromApi = async (input) => {
  try {
    console.log(
      `Workflow Design Submission - getWorkflowDesignTicketFromApi - input: ${JSON.stringify(
        input
      )}`
    );

    const apiPayload = {
      query: GET_WORKFLOW_DESIGN_TICKET_QUERY,
      variables: {
        ticketId: input.ticketId,
        tenantId: input.tenantId,
      },
    };

    const response = await workflowDesignSubmissionClient.query(apiPayload);
    console.log(
      `Workflow Design Submission - getWorkflowDesignTicketFromApi - response: ${JSON.stringify(
        response
      )}`
    );

    // Map ticket data back to component format
    return mapTicketToComponent(response.data.getInitialDesignLayoutTicket);
  } catch (error) {
    console.error(
      `Workflow Design Submission - getWorkflowDesignTicketFromApi - error: ${JSON.stringify(
        error
      )}`
    );

    // Handle GraphQL errors for nullable fields
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      const hasNullableFieldError = error.graphQLErrors.some((err) =>
        err.message.includes("Cannot return null for non-nullable type")
      );

      if (hasNullableFieldError) {
        console.log(
          "Handling nullable field error, creating fallback ticket data"
        );

        // Create a fallback ticket object with default values
        const fallbackTicket = {
          ticketId: input.ticketId,
          tenantId: input.tenantId,
          status: "pending",
          workflowTitle: "Unknown Workflow",
          summaryNarrative: "No description available",
          priority: "medium",
          source: "workflow_design_summary",
          sourceScreen: "workflow_design_summary_page",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          completedAt: null,
          executionArn: null,
          designId: null,
          workflowEntityId: null,
          metadata: JSON.stringify({}),
        };

        console.log("Created fallback ticket:", fallbackTicket);
        return mapTicketToComponent(fallbackTicket);
      }
    }

    throw error;
  }
};

// List workflow design tickets for a tenant
export const listWorkflowDesignTicketsFromApi = async (input) => {
  try {
    console.log(
      `Workflow Design Submission - listWorkflowDesignTicketsFromApi - input: ${JSON.stringify(
        input
      )}`
    );

    const apiPayload = {
      query: LIST_WORKFLOW_DESIGN_TICKETS_QUERY,
      variables: {
        tenantId: input.tenantId,
        status: input.status || null,
        limit: input.limit || 50,
      },
    };

    const response = await workflowDesignSubmissionClient.query(apiPayload);
    console.log(
      `Workflow Design Submission - listWorkflowDesignTicketsFromApi - response: ${JSON.stringify(
        response
      )}`
    );

    // Map tickets data back to component format
    const tickets =
      response.data.listInitialDesignLayoutTickets.map(mapTicketToComponent);

    return {
      tickets,
      totalCount: tickets.length,
      hasMore: false, // The schema doesn't indicate pagination
    };
  } catch (error) {
    console.error(
      `Workflow Design Submission - listWorkflowDesignTicketsFromApi - error: ${JSON.stringify(
        error
      )}`
    );
    throw error;
  }
};

// Check workflow design status (using execution ARN)
export const checkWorkflowDesignStatusFromApi = async (executionArn) => {
  try {
    console.log(
      `Workflow Design Submission - checkWorkflowDesignStatusFromApi - executionArn: ${executionArn}`
    );

    // This would typically call a separate status checking API
    // For now, we'll return a mock status
    return {
      executionArn,
      status: "running",
      startDate: new Date().toISOString(),
      endDate: null,
      input: {},
      output: null,
      error: null,
    };
  } catch (error) {
    console.error(
      `Workflow Design Submission - checkWorkflowDesignStatusFromApi - error: ${JSON.stringify(
        error
      )}`
    );
    throw error;
  }
};

// Get workflow design statistics
export const getWorkflowDesignStatsFromApi = async (input) => {
  try {
    console.log(
      `Workflow Design Submission - getWorkflowDesignStatsFromApi - input: ${JSON.stringify(
        input
      )}`
    );

    // Get all tickets for the tenant
    const ticketsResponse = await listWorkflowDesignTicketsFromApi(input);
    const tickets = ticketsResponse.tickets;

    return {
      totalTickets: tickets.length,
      completedTickets: tickets.filter((ticket) => ticket.isCompleted).length,
      processingTickets: tickets.filter((ticket) => ticket.isProcessing).length,
      pendingTickets: tickets.filter((ticket) => ticket.isPending).length,
      failedTickets: tickets.filter((ticket) => ticket.isFailed).length,
      averageProcessingTime:
        tickets
          .filter((ticket) => ticket.processingTime)
          .reduce((sum, ticket) => sum + ticket.processingTime, 0) /
          tickets.filter((ticket) => ticket.processingTime).length || 0,
      lastUpdated: new Date().toISOString(),
    };
  } catch (error) {
    console.error(
      `Workflow Design Submission - getWorkflowDesignStatsFromApi - error: ${JSON.stringify(
        error
      )}`
    );
    throw error;
  }
};
